interface JWTPayload {
    exp?: number;
    iat?: number;
    [key: string]: any;
}

/**
 * Decode a JWT token without verifying the signature
 * This is safe for reading expiration time on the client side
 */
export function decodeJWT(token: string): JWTPayload | null {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        
        // Decode the payload (second part)
        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    } catch (error) {
        console.error('Failed to decode JWT:', error);
        return null;
    }
}

/**
 * Check if a JWT token is expired
 * @param token The JWT token to check
 * @param bufferSeconds Optional buffer time in seconds (default: 60)
 * @returns true if token is expired or invalid
 */
export function isTokenExpired(token: string | undefined, bufferSeconds: number = 60): boolean {
    if (!token) {
        return true;
    }
    
    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
        // If we can't decode or no expiration, consider it expired
        return true;
    }
    
    // Check if token is expired (with buffer)
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp - bufferSeconds;
    
    return currentTime > expirationTime;
}

/**
 * Get the remaining time until token expiration in seconds
 */
export function getTokenTimeRemaining(token: string): number {
    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
        return 0;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    return Math.max(0, payload.exp - currentTime);
}